/**
 * DraftSelectField Component
 * 
 * A select field that integrates with the existing draft state pattern used in the app.
 */

import React from 'react';
import { Grid } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { SelectField, type SelectOption } from './SelectField';

export interface DraftSelectFieldProps<State extends Record<string, unknown>> {
  /** Field label */
  label: string;
  /** State key to update */
  stateKey: string & keyof State;
  /** Draft state tuple [state, setState] */
  draft: [State, React.Dispatch<React.SetStateAction<State>>];
  /** Available options */
  options: SelectOption[];
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Helper text */
  helperText?: string;
  /** Grid size (out of 12) */
  size?: number;
  /** Whether to default to first option */
  defaultFirst?: boolean;
  /** Additional styles */
  sx?: SxProps<Theme>;
}

export const DraftSelectField = <State extends Record<string, unknown>>({
  label,
  stateKey,
  draft,
  options,
  disabled = false,
  required = false,
  placeholder,
  helperText,
  size = 12,
  defaultFirst = true,
  sx,
}: DraftSelectFieldProps<State>) => {
  const [draftState, setDraftState] = draft;

  const currentValue = draftState?.[stateKey] ?? (defaultFirst ? options[0]?.value : '');

  const handleChange = (value: string | number) => {
    setDraftState((prev) => ({
      ...prev,
      [stateKey]: value,
    }));
  };

  const hasError = required && !currentValue;

  return (
    <Grid item xs={12} sm={12} md={size} sx={sx}>
      <SelectField
        label={label}
        value={currentValue as string | number}
        onChange={handleChange}
        options={options}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        helperText={helperText}
        error={hasError}
        errorMessage={hasError ? 'This field is required' : undefined}
      />
    </Grid>
  );
};

export default DraftSelectField;

